// The first review of a signed-out visitor's first set, fixed before they sign up (T1's "save first" side, everyone's
// since 2026-10-19; the file keeps the name of T1's other side, "answer first", which ended with it).
//
// The review is fixed when the visitor presses "save questions and start answering" on the questions screen: its
// questions and their order, the ones the screen previewed. The visitor signs up there, and the review then opens at
// question 1 for the account. So the review's question ids, in order, are kept here, in a small store mirrored to the
// tab's sessionStorage: a reload, a sign-in window or a sign-up that recreates the review's card list then rebuilds
// the same review (orderCardsLikeTheAnswerFirstReview, getAnswerFirstReviewResumeIndex). Once the account has the
// review, its answers are noted here too, so a reload picks the review up where it was and its progress is counted
// (firstReviewProgressDenominator.ts).
//
// Kept for the tab only, for 24 hours, like the anonymous upload it came from (anonymous-upload-claims.ts):
// card ids, the rating and the chosen answer's text. Nothing else about the visitor.
//
// No React and nothing large in here: the answer path (helper-methods.ts) is in the app's startup.

import { create } from "zustand";

const ANSWER_FIRST_REVIEW_STORAGE_KEY = "jungle:answer-first-review:v1";
const ANSWER_FIRST_REVIEW_MAXIMUM_AGE_MILLISECONDS = 24 * 60 * 60 * 1000;

export type AnswerFirstReviewAnswer = {
  understandingRating: number;
  answerText: string;
  answeredAtMilliseconds: number;
};

export type AnswerFirstReview = {
  deckId: string;
  orderedCardIds: string[];
  answersByCardId: Record<string, AnswerFirstReviewAnswer>;
  startedAtMilliseconds: number;
  /** The account that signed up (or logged in) for the review, once it has it */
  answersGivenToAccountId: string | null;
  /**
   * Whether the account would have had this set's first review anyway (a new account, or one that has not had a first
   * review). False for an account that logged in at the sign-up after finishing a first review long ago: what follows
   * a first review (the streak day, the exam date, the upsell) is not shown to it.
   */
  isTheAccountsFirstReviewInTodaysFlow: boolean | null;
  /** When the set was saved to the account (claimed, and its progress moved to the account) */
  answersSavedToAccountAtMilliseconds: number | null;
  /** When the first review ended (its last question answered and its checkpoint passed) */
  endedAtMilliseconds: number | null;
  /**
   * The first review's progress bar counts the review's progress out of the set's questions: these are the set's
   * other questions it counts, each with the most it can add to the progress, noted when question 1 first shows
   * (firstReviewProgressDenominator.ts). Null until then.
   */
  maximumProgressOfTheSetsOtherQuestions: Record<string, number> | null;
};

type AnswerFirstReviewStore = {
  answerFirstReview: AnswerFirstReview | null;
};

function isAnswer(value: unknown): value is AnswerFirstReviewAnswer {
  const answer = value as AnswerFirstReviewAnswer;
  return (
    Boolean(answer) &&
    Number.isFinite(answer.understandingRating) &&
    typeof answer.answerText === "string" &&
    Number.isFinite(answer.answeredAtMilliseconds)
  );
}

function isAnswerMap(value: unknown): value is Record<string, AnswerFirstReviewAnswer> {
  return value !== null && typeof value === "object" && Object.values(value).every(isAnswer);
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function isMaximumProgressByCardId(value: unknown): value is Record<string, number> {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (maximumProgress) => typeof maximumProgress === "number" && Number.isFinite(maximumProgress)
    )
  );
}

/**
 * The stored review, or null when there is none, it is too old, or it is not one this code wrote.
 *
 * A review with answers but no account is one T1's "answer first" side began (its visitor answered three questions
 * before signing up), left in a tab when T1 ended (2026-10-19). Its answers are dropped: the review starts again at
 * question 1, under the sign-up sheet, as for any visitor who reaches the first review signed out.
 *
 * One that side's visitor had already given to an account, but whose save of the answers given before signing up
 * had not finished (`answersGivenBeforeSigningUp` without `answersSavedToAccountAtMilliseconds`), would otherwise
 * show those questions as answered while nothing saves them any more: those answers are dropped too, so the
 * review asks those questions again for the account, and the save that is left claims the set.
 */
export function parseStoredAnswerFirstReview(
  storedValue: string | null,
  nowMilliseconds: number
): AnswerFirstReview | null {
  if (!storedValue) return null;
  try {
    const stored = JSON.parse(storedValue) as AnswerFirstReview;
    const isWithinTheRetentionWindow =
      Number.isFinite(stored?.startedAtMilliseconds) &&
      stored.startedAtMilliseconds <= nowMilliseconds &&
      nowMilliseconds - stored.startedAtMilliseconds < ANSWER_FIRST_REVIEW_MAXIMUM_AGE_MILLISECONDS;
    const hasItsQuestions =
      typeof stored?.deckId === "string" &&
      Array.isArray(stored.orderedCardIds) &&
      stored.orderedCardIds.every((cardId) => typeof cardId === "string");
    const hasValidAnswers = isAnswerMap(stored?.answersByCardId);
    if (!isWithinTheRetentionWindow || !hasItsQuestions || !hasValidAnswers) return null;
    const answersGivenToAccountId =
      typeof stored.answersGivenToAccountId === "string" ? stored.answersGivenToAccountId : null;
    const answersSavedToAccountAtMilliseconds = finiteNumberOrNull(stored.answersSavedToAccountAtMilliseconds);
    const answersAnsweredFirstStillUnsaved =
      answersSavedToAccountAtMilliseconds === null
        ? ((stored as { answersGivenBeforeSigningUp?: unknown }).answersGivenBeforeSigningUp ?? {})
        : {};
    const cardIdsAnsweredFirstStillUnsaved = new Set(
      answersAnsweredFirstStillUnsaved && typeof answersAnsweredFirstStillUnsaved === "object"
        ? Object.keys(answersAnsweredFirstStillUnsaved)
        : []
    );
    return {
      deckId: stored.deckId,
      orderedCardIds: stored.orderedCardIds,
      answersByCardId: answersGivenToAccountId
        ? Object.fromEntries(
            Object.entries(stored.answersByCardId).filter(([cardId]) => !cardIdsAnsweredFirstStillUnsaved.has(cardId))
          )
        : {},
      startedAtMilliseconds: stored.startedAtMilliseconds,
      answersGivenToAccountId,
      isTheAccountsFirstReviewInTodaysFlow:
        typeof stored.isTheAccountsFirstReviewInTodaysFlow === "boolean"
          ? stored.isTheAccountsFirstReviewInTodaysFlow
          : null,
      answersSavedToAccountAtMilliseconds,
      endedAtMilliseconds: finiteNumberOrNull(stored.endedAtMilliseconds),
      maximumProgressOfTheSetsOtherQuestions: isMaximumProgressByCardId(stored.maximumProgressOfTheSetsOtherQuestions)
        ? stored.maximumProgressOfTheSetsOtherQuestions
        : null,
    };
  } catch {
    return null;
  }
}

function readStoredAnswerFirstReview(): AnswerFirstReview | null {
  try {
    return parseStoredAnswerFirstReview(window.sessionStorage.getItem(ANSWER_FIRST_REVIEW_STORAGE_KEY), Date.now());
  } catch {
    return null;
  }
}

export function storeAnswerFirstReview(answerFirstReview: AnswerFirstReview | null): void {
  useAnswerFirstReviewStore.setState({ answerFirstReview });
  try {
    if (answerFirstReview) {
      window.sessionStorage.setItem(ANSWER_FIRST_REVIEW_STORAGE_KEY, JSON.stringify(answerFirstReview));
    } else {
      window.sessionStorage.removeItem(ANSWER_FIRST_REVIEW_STORAGE_KEY);
    }
  } catch {
    // Storage can be blocked: the review still works in this page, it just does not survive a reload
  }
}

export const useAnswerFirstReviewStore = create<AnswerFirstReviewStore>(() => ({
  answerFirstReview: typeof window === "undefined" ? null : readStoredAnswerFirstReview(),
}));

/** The visitor pressed "save questions and start answering": the review's questions, in the order they will be asked. */
export function startAnswerFirstReview(deckId: string, orderedCardIds: string[], nowMilliseconds = Date.now()): void {
  storeAnswerFirstReview({
    deckId,
    orderedCardIds,
    answersByCardId: {},
    startedAtMilliseconds: nowMilliseconds,
    answersGivenToAccountId: null,
    isTheAccountsFirstReviewInTodaysFlow: null,
    answersSavedToAccountAtMilliseconds: null,
    endedAtMilliseconds: null,
    maximumProgressOfTheSetsOtherQuestions: null,
  });
}

/**
 * Questions that reached the review after it began (it keeps growing to its full length while the set is still
 * being written, as any first review does): they go at its end, after the questions already in it.
 */
export function addQuestionsToAnswerFirstReview(cardIds: string[]): void {
  const { answerFirstReview } = useAnswerFirstReviewStore.getState();
  if (!answerFirstReview) return;
  const cardIdsNotYetInTheReview = cardIds.filter(
    (cardId, index) =>
      typeof cardId === "string" &&
      !answerFirstReview.orderedCardIds.includes(cardId) &&
      cardIds.indexOf(cardId) === index
  );
  if (!cardIdsNotYetInTheReview.length) return;
  storeAnswerFirstReview({
    ...answerFirstReview,
    orderedCardIds: [...answerFirstReview.orderedCardIds, ...cardIdsNotYetInTheReview],
  });
}

/** The first review of this set fixed before signing up, if this tab has one. */
export function getAnswerFirstReview(deckId: string | null | undefined): AnswerFirstReview | null {
  const { answerFirstReview } = useAnswerFirstReviewStore.getState();
  if (!deckId || !answerFirstReview || answerFirstReview.deckId !== deckId) return null;
  return answerFirstReview;
}

/**
 * Notes an answer to one of the review's questions (for the review picked up after a reload, and its counted
 * progress). A question answered again keeps its latest answer. Answers to other questions are ignored. (Only an
 * account answers them: signed out, the review's questions wait under the sign-up sheet.)
 */
export function noteAnswerInAnswerFirstReview(
  cardId: string | null | undefined,
  understandingRating: number,
  answerText: string,
  nowMilliseconds = Date.now()
): void {
  const { answerFirstReview } = useAnswerFirstReviewStore.getState();
  if (!cardId || !answerFirstReview || !answerFirstReview.orderedCardIds.includes(cardId)) return;
  // The same answer noted again keeps the moment it was given
  const answerAlreadyNoted = answerFirstReview.answersByCardId[cardId];
  if (answerAlreadyNoted?.understandingRating === understandingRating && answerAlreadyNoted.answerText === answerText) {
    return;
  }
  storeAnswerFirstReview({
    ...answerFirstReview,
    answersByCardId: {
      ...answerFirstReview.answersByCardId,
      [cardId]: { understandingRating, answerText, answeredAtMilliseconds: nowMilliseconds },
    },
  });
}

/** The account that signed up (or logged in) now has the review, from question 1. */
export function noteAnswerFirstReviewAnswersGivenToAccount(
  accountId: string,
  isTheAccountsFirstReviewInTodaysFlow = true
): void {
  const { answerFirstReview } = useAnswerFirstReviewStore.getState();
  if (!answerFirstReview) return;
  storeAnswerFirstReview({
    ...answerFirstReview,
    answersGivenToAccountId: accountId,
    isTheAccountsFirstReviewInTodaysFlow,
  });
}

/**
 * False only when the first review on screen is one fixed before signing up that its account would not otherwise
 * have had (see isTheAccountsFirstReviewInTodaysFlow); true otherwise, so nothing changes for any other review.
 */
export function isTheFirstReviewOnScreenTheAccountsFirstReview(deckId: string | null | undefined): boolean {
  return getAnswerFirstReview(deckId)?.isTheAccountsFirstReviewInTodaysFlow !== false;
}

/** The set is saved to the account (saveAnswerFirstAnswersToTheAccount retries until it is). */
export function noteAnswerFirstReviewAnswersSavedToAccount(nowMilliseconds = Date.now()): void {
  const { answerFirstReview } = useAnswerFirstReviewStore.getState();
  if (!answerFirstReview || answerFirstReview.answersSavedToAccountAtMilliseconds) return;
  storeAnswerFirstReview({ ...answerFirstReview, answersSavedToAccountAtMilliseconds: nowMilliseconds });
}

/** The first review of this set ended (HelperMethodsHook): from now on the set is reviewed like any other. */
export function noteAnswerFirstReviewEnded(deckId: string | null | undefined, nowMilliseconds = Date.now()): void {
  const answerFirstReview = getAnswerFirstReview(deckId);
  if (!answerFirstReview || answerFirstReview.endedAtMilliseconds) return;
  storeAnswerFirstReview({ ...answerFirstReview, endedAtMilliseconds: nowMilliseconds });
}

/** The set's other questions the first review's progress is counted out of (firstReviewProgressDenominator.ts). */
export function noteTheSetsOtherQuestionsInTheFirstReviewsProgress(
  deckId: string | null | undefined,
  maximumProgressOfTheSetsOtherQuestions: Record<string, number>
): void {
  const answerFirstReview = getAnswerFirstReview(deckId);
  if (!answerFirstReview || answerFirstReview.endedAtMilliseconds) return;
  storeAnswerFirstReview({ ...answerFirstReview, maximumProgressOfTheSetsOtherQuestions });
}

/** The first review of this set fixed before signing up, while it is the set's first review (it has not ended). */
export function getAnswerFirstReviewInProgress(deckId: string | null | undefined): AnswerFirstReview | null {
  const answerFirstReview = getAnswerFirstReview(deckId);
  return answerFirstReview && !answerFirstReview.endedAtMilliseconds ? answerFirstReview : null;
}

export function forgetAnswerFirstReview(): void {
  storeAnswerFirstReview(null);
}

/**
 * The cards in the review's order: its questions first, as they were asked, then any other card in the order
 * given (a question that arrived later, which a review may add at its end, never before).
 */
export function orderCardsLikeTheAnswerFirstReview<Card extends { id?: string }>(
  cards: Card[],
  answerFirstReview: AnswerFirstReview
): Card[] {
  const cardsById = new Map(cards.filter((card) => card?.id).map((card) => [card.id as string, card]));
  const cardsOfTheReview = answerFirstReview.orderedCardIds
    .map((cardId) => cardsById.get(cardId))
    .filter((card): card is Card => Boolean(card));
  const otherCards = cards.filter((card) => !card?.id || !answerFirstReview.orderedCardIds.includes(card.id));
  return [...cardsOfTheReview, ...otherCards];
}

/** Where to pick the review up: its first question without an answer (the last one when all have one). */
export function getAnswerFirstReviewResumeIndex<Card extends { id?: string }>(
  cardsInReviewOrder: Card[],
  answerFirstReview: AnswerFirstReview
): number {
  const indexOfFirstUnansweredCard = cardsInReviewOrder.findIndex(
    (card) => !card?.id || !answerFirstReview.answersByCardId[card.id]
  );
  if (indexOfFirstUnansweredCard === -1) return Math.max(0, cardsInReviewOrder.length - 1);
  return indexOfFirstUnansweredCard;
}
